import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import StatusChip from '../components/shared/StatusChip';
import TaskExecutor from '../components/nurse/TaskExecutor';
import {
  ArrowLeft, Check, Heart, Thermometer, Activity, Wind, Droplets, Ruler, Weight, AlertCircle,
  ClipboardList, CheckCircle2, Circle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function NurseIntake() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [visitTypes, setVisitTypes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const isTasksMode = appointment?.status === 'tasks_pending';

  // Intake form state
  const [actualVisitType, setActualVisitType] = useState('');
  const [primaryReason, setPrimaryReason] = useState('');
  const [secondaryReason, setSecondaryReason] = useState('');
  const [tertiaryReason, setTertiaryReason] = useState('');
  const [freeNote, setFreeNote] = useState('');
  const [vitals, setVitals] = useState({
    bp_systolic: '', bp_diastolic: '', pulse: '', temp: '',
    resp_rate: '', o2_sat: '', weight: '', height: '', pain_score: '',
  });
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [intakeRes, vtRes] = await Promise.all([
        api.get(`/appointments/${appointmentId}/nurse-intake`),
        api.get('/visit-types'),
      ]);
      const appt = intakeRes.data.appointment;
      const pt = intakeRes.data.patient;
      const intake = intakeRes.data.nurse_intake || {};

      setAppointment(appt);
      setPatient(pt);
      setVisitTypes(vtRes.data.visit_types || []);
      setSelectedRoom(appt?.room || '');

      // Load rooms for this provider
      if (appt?.provider_id) {
        try {
          const roomRes = await api.get(`/rooms/for-provider/${appt.provider_id}`);
          setRooms(roomRes.data.rooms || []);
        } catch {
          try { const r = await api.get('/rooms'); setRooms(r.data.rooms || []); } catch {}
        }
      }

      // Pre-fill from existing intake data
      setActualVisitType(intake.actual_visit_type || appt?.visit_type || '');
      setPrimaryReason(intake.primary_reason || '');
      setSecondaryReason(intake.secondary_reason || '');
      setTertiaryReason(intake.tertiary_reason || '');
      setFreeNote(intake.free_note || '');
      if (intake.vitals) {
        setVitals(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(intake.vitals).map(([k, v]) => [k, v?.toString() || '']))
        }));
      }

      // Auto-transition to in_triage if ready_for_nurse
      if (['ready_for_nurse', 'ready'].includes(appt?.status)) {
        try {
          await api.post(`/appointments/${appointmentId}/nurse-transition`, { target_state: 'in_triage' });
        } catch {}
      }
      
      // Load tasks if in tasks_pending mode
      if (appt?.status === 'tasks_pending') {
        try {
          const taskRes = await api.get(`/appointments/${appointmentId}/tasks`);
          setTasks(taskRes.data.tasks || []);
        } catch {}
      }
    } catch (err) {
      toast.error('Failed to load appointment');
    } finally { setLoading(false); }
  }, [appointmentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVitalChange = (field, value) => {
    setVitals(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const vitalsPayload = {};
      for (const [k, v] of Object.entries(vitals)) {
        if (v !== '' && v !== null) {
          vitalsPayload[k] = ['temp', 'o2_sat', 'weight', 'height'].includes(k) ? parseFloat(v) : parseInt(v);
        }
      }
      await api.put(`/appointments/${appointmentId}/nurse-intake`, {
        actual_visit_type: actualVisitType || undefined,
        primary_reason: primaryReason || undefined,
        secondary_reason: secondaryReason || undefined,
        tertiary_reason: tertiaryReason || undefined,
        free_note: freeNote || undefined,
        vitals: Object.keys(vitalsPayload).length > 0 ? vitalsPayload : undefined,
      });
      toast.success('Intake saved');
    } catch (err) {
      toast.error('Failed to save intake');
    } finally { setSaving(false); }
  };

  const handleComplete = async () => {
    if (!actualVisitType) { toast.error('Actual Visit Type is required'); return; }
    if (!primaryReason) { toast.error('Primary Reason is required'); return; }
    setCompleting(true);
    try {
      await handleSave();
      await api.post(`/appointments/${appointmentId}/complete-triage`);
      toast.success('Triage complete — patient ready for provider');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete triage');
    } finally { setCompleting(false); }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const res = await api.post(`/appointments/${appointmentId}/tasks/${taskId}/complete`);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed', completed_at: new Date().toISOString() } : t));
      if (res.data.remaining_pending === 0) {
        toast.success('All tasks complete — ready for checkout');
        setAppointment(res.data.appointment);
      } else {
        toast.success(`Task complete (${res.data.remaining_pending} remaining)`);
      }
    } catch (err) {
      toast.error('Failed to complete task');
    }
  };

  const age = patient?.dob ? Math.floor((Date.now() - new Date(patient.dob).getTime()) / (365.25 * 86400000)) : '?';

  if (loading) return <div className="p-6 text-muted-foreground">Loading intake...</div>;
  if (!appointment) return <div className="p-6 text-red-500">Appointment not found</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4" data-testid="nurse-intake-page">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1 text-xs" data-testid="nurse-intake-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Queue
      </Button>

      {/* Section A: Patient Header */}
      <Card className="border" data-testid="nurse-intake-header">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                {patient?.name?.first?.[0]}{patient?.name?.last?.[0]}
              </div>
              <div>
                <h1 className="text-lg font-bold">{patient?.name?.first} {patient?.name?.last}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px] font-mono">{patient?.mrn}</Badge>
                  <span className="text-xs text-muted-foreground">{patient?.dob} ({age}y) - {patient?.sex}</span>
                  {appointment.room && <span className="text-xs text-muted-foreground">Room: {appointment.room}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{appointment.visit_type}</Badge>
              <StatusChip status={appointment.status} />
            </div>
          </div>
          {(patient?.allergies || []).length > 0 && (
            <div className="mt-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs text-amber-700 font-medium">Allergies: {patient.allergies.map(a => a.name).join(', ')}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section B: Visit Type + Chief Complaint */}
      <Card className="border" data-testid="nurse-intake-visit-info">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm">Visit Information</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-4 px-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Scheduled Visit Type</Label>
              <Input value={appointment.visit_type || ''} disabled className="text-sm bg-muted/30 mt-1" data-testid="scheduled-visit-type" />
            </div>
            <div>
              <Label className="text-xs">Actual Visit Type <span className="text-red-500">*</span></Label>
              <Select value={actualVisitType} onValueChange={setActualVisitType}>
                <SelectTrigger className="mt-1 text-sm" data-testid="actual-visit-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {visitTypes.map(vt => <SelectItem key={vt.id || vt.name} value={vt.name}>{vt.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Room</Label>
              <Select value={selectedRoom} onValueChange={v => { setSelectedRoom(v); api.put(`/appointments/${appointmentId}/nurse-intake`, { room: v }).catch(() => {}); }}>
                <SelectTrigger className="mt-1 text-sm" data-testid="room-select">
                  <SelectValue placeholder="Assign room..." />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map(r => <SelectItem key={r.id} value={r.name}>{r.name} {r.is_shared ? '(shared)' : ''}</SelectItem>)}
                  {rooms.length === 0 && <SelectItem value="none" disabled>No rooms configured</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Primary Reason <span className="text-red-500">*</span></Label>
            <Input value={primaryReason} onChange={e => setPrimaryReason(e.target.value)}
              placeholder="Chief complaint / reason for visit" className="text-sm mt-1" data-testid="primary-reason" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Secondary Reason</Label>
              <Input value={secondaryReason} onChange={e => setSecondaryReason(e.target.value)}
                placeholder="Optional" className="text-sm mt-1" data-testid="secondary-reason" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Tertiary Reason</Label>
              <Input value={tertiaryReason} onChange={e => setTertiaryReason(e.target.value)}
                placeholder="Optional" className="text-sm mt-1" data-testid="tertiary-reason" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Free Note</Label>
            <Textarea value={freeNote} onChange={e => setFreeNote(e.target.value)}
              placeholder="Additional notes..." className="text-sm mt-1 min-h-[60px]" data-testid="free-note" />
          </div>
        </CardContent>
      </Card>

      {/* Section C: Vitals */}
      <Card className="border" data-testid="nurse-intake-vitals">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-red-500" /> Vitals
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-4 px-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><Heart className="w-3 h-3 text-red-400" /> Blood Pressure</Label>
              <div className="flex items-center gap-1 mt-1">
                <Input type="number" value={vitals.bp_systolic} onChange={e => handleVitalChange('bp_systolic', e.target.value)}
                  placeholder="SYS" className="text-sm w-20" data-testid="vitals-bp-systolic" />
                <span className="text-muted-foreground">/</span>
                <Input type="number" value={vitals.bp_diastolic} onChange={e => handleVitalChange('bp_diastolic', e.target.value)}
                  placeholder="DIA" className="text-sm w-20" data-testid="vitals-bp-diastolic" />
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Activity className="w-3 h-3 text-blue-400" /> Pulse</Label>
              <Input type="number" value={vitals.pulse} onChange={e => handleVitalChange('pulse', e.target.value)}
                placeholder="bpm" className="text-sm mt-1" data-testid="vitals-pulse" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Thermometer className="w-3 h-3 text-orange-400" /> Temp</Label>
              <Input type="number" step="0.1" value={vitals.temp} onChange={e => handleVitalChange('temp', e.target.value)}
                placeholder="F" className="text-sm mt-1" data-testid="vitals-temp" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Wind className="w-3 h-3 text-teal-400" /> Resp Rate</Label>
              <Input type="number" value={vitals.resp_rate} onChange={e => handleVitalChange('resp_rate', e.target.value)}
                placeholder="/min" className="text-sm mt-1" data-testid="vitals-resp-rate" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Droplets className="w-3 h-3 text-blue-400" /> O2 Sat</Label>
              <Input type="number" step="0.1" value={vitals.o2_sat} onChange={e => handleVitalChange('o2_sat', e.target.value)}
                placeholder="%" className="text-sm mt-1" data-testid="vitals-o2-sat" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-400" /> Pain Score</Label>
              <Select value={vitals.pain_score?.toString() || ''} onValueChange={v => handleVitalChange('pain_score', v)}>
                <SelectTrigger className="mt-1 text-sm" data-testid="vitals-pain-score">
                  <SelectValue placeholder="0-10" />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(11)].map((_, i) => <SelectItem key={i} value={i.toString()}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Weight className="w-3 h-3 text-slate-400" /> Weight</Label>
              <Input type="number" step="0.1" value={vitals.weight} onChange={e => handleVitalChange('weight', e.target.value)}
                placeholder="lbs" className="text-sm mt-1" data-testid="vitals-weight" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Ruler className="w-3 h-3 text-slate-400" /> Height</Label>
              <Input type="number" step="0.1" value={vitals.height} onChange={e => handleVitalChange('height', e.target.value)}
                placeholder="in" className="text-sm mt-1" data-testid="vitals-height" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section E: Tasks (Phase 3 — visible when tasks_pending) */}
      {(isTasksMode || tasks.length > 0) && (
        <Card className="border border-orange-200" data-testid="nurse-intake-tasks">
          <CardHeader className="py-3 px-4 bg-orange-50/50">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-orange-500" /> Pending Tasks
              <span className="text-xs text-muted-foreground ml-auto">
                {tasks.filter(t => t.status === 'completed').length}/{tasks.length} complete
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 pb-4 px-4 space-y-2">
            {tasks.map(task => {
              const handleTaskComplete = (result) => {
                setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'completed', result_summary: result?.result?.summary } : t));
                if (result?.remaining_pending === 0) {
                  toast.success('All tasks complete — ready for checkout');
                  setAppointment(prev => prev ? {...prev, status: 'ready_for_checkout'} : prev);
                }
                fetchData();
              };
              return (
              <div key={task.id} data-testid={`task-item-${task.id}`}>
                <div className={`flex items-center gap-3 p-2 rounded border ${task.status === 'completed' ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white'}`}>
                  {task.status === 'completed' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-orange-400 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                      task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                    }`}>{task.task_type}</span>
                    {task.description && task.description !== task.task_type && (
                      <span className="text-xs text-muted-foreground ml-2">{task.description}</span>
                    )}
                    {task.result_summary && <span className="text-[10px] text-emerald-600 ml-2">{task.result_summary}</span>}
                  </div>
                  {task.status === 'pending' && (
                    <div className="flex gap-1">
                      <TaskExecutor task={task} appointmentId={appointmentId} onComplete={handleTaskComplete} />
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                        onClick={() => handleCompleteTask(task.id)}
                        data-testid={`complete-task-${task.id}`}>
                        <Check className="w-3 h-3" /> Done
                      </Button>
                    </div>
                  )}
                  {task.status === 'completed' && (
                    <span className="text-[10px] text-emerald-600">{task.result_summary || 'Done'}</span>
                  )}
                </div>
                {/* Executable task form renders below the task row */}
              </div>
              );
            })}
            {tasks.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">No tasks assigned</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section D: Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="nurse-intake-save">
          {saving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button size="default" onClick={handleComplete} disabled={completing}
          className="gap-2" data-testid="nurse-intake-complete">
          <Check className="w-4 h-4" />
          {completing ? 'Completing...' : 'Mark Ready for Provider'}
        </Button>
      </div>
    </div>
  );
}
